import React, { Suspense, useEffect, useRef } from 'react';
import { Routes as RouterRoutes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AUTHENTICATED_ENTRY } from 'configs/AppConfig';
import { protectedRoutes, publicRoutes } from 'configs/RoutesConfig';
import ProtectedRoute from './ProtectedRoute';
import PublicRoute from './PublicRoute';
import AppRoute from './AppRoute';

// Si una pantalla recién abierta tira 403 en sus fetch iniciales (se entró a
// una sección sin permiso, ej. por URL directa), volvemos a la vista anterior
// en vez de dejar la página rota. El toast lo pone el FetchInterceptor, que
// además ya NO desloguea por 403. Solo reacciona a 403s de los primeros
// segundos tras navegar: un 403 de un botón dentro de una pantalla ya abierta
// muestra el error y no te mueve de ahí. Debounced: una pantalla sin permiso
// suele fallar varios fetch a la vez y hay que volver UNA sola vista.
const PermissionDeniedRedirect = () => {
	const location = useLocation()
	const navigate = useNavigate()
	const lastNavAt = useRef(Date.now())
	const lastBackAt = useRef(0)

	useEffect(() => { lastNavAt.current = Date.now() }, [location])

	useEffect(() => {
		const onDenied = () => {
			const now = Date.now()
			const reciénNavegado = now - lastNavAt.current < 4000
			const yaVolvimos = now - lastBackAt.current < 2000
			if (!reciénNavegado || yaVolvimos) return
			lastBackAt.current = now
			// idx > 0 = hay historial propio de la app; si se entró directo por
			// URL, no hay "anterior" nuestro y se va al inicio.
			if (window.history.state?.idx > 0) navigate(-1)
			else navigate('/', { replace: true })
		}
		window.addEventListener('api-permission-denied', onDenied)
		return () => window.removeEventListener('api-permission-denied', onDenied)
	}, [navigate])

	return null
}

// Consent OAuth (conexión con Claude via MCP). Lazy: solo se carga cuando un
// cliente MCP abre el browser para autorizar.
const OAuthConsentView = React.lazy(() => import('views/oauth-consent'));

const Routes = () => {

	return (
		<>
		<PermissionDeniedRedirect />
		<RouterRoutes>
			{/*
				/oauth/consent va suelta, fuera de ProtectedRoute y PublicRoute:
				Protected perdería los query params OAuth en el redirect de login y
				Public rebotaría a los usuarios ya logueados al dashboard. La página
				maneja el auth por su cuenta.
			*/}
			<Route
				path="/oauth/consent"
				element={
					<Suspense fallback={null}>
						<OAuthConsentView />
					</Suspense>
				}
			/>
			<Route path="/" element={<ProtectedRoute />}>
				<Route path="/" element={<Navigate replace to={AUTHENTICATED_ENTRY} />} />
				{protectedRoutes.map((route, index) => {
					return (
						<Route 
							key={route.key + index} 
							path={route.path}
							element={
								<AppRoute
									routeKey={route.key} 
									component={route.component}
									{...route.meta} 
								/>
							}
						/>
					)
				})}
				<Route path="*" element={<Navigate to="/" replace />} />
			</Route>
			<Route path="/" element={<PublicRoute />}>
				{publicRoutes.map(route => {
					return (
						<Route 
							key={route.path} 
							path={route.path}
							element={
								<AppRoute
									routeKey={route.key} 
									component={route.component}
									{...route.meta} 
								/>
							}
						/ >
					)
				})}
			</Route>
		</RouterRoutes>
		</>
	)
}

export default Routes